import { ApplicationConfig, provideBrowserGlobalErrorListeners, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { baseHeadersInterceptor } from './core/interceptors/base-headers.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorMappingInterceptor } from './core/interceptors/error-mapping.interceptor';
import { retryInterceptor } from './core/interceptors/retry.interceptor';
import { MaintenanceCheckService } from './core/services/maintenance-check.service';
import { AppConfigService } from './core/services/app-config.service';
import { take } from 'rxjs/operators';
import { environment } from '../environments/environment';

// NgRx Store
import { authReducer } from './store/auth/auth.reducer';
import { deviceReducer } from './store/device/device.reducer';
import { dashboardReducer } from './store/dashboard/dashboard.reducer';
import { uiReducer } from './store/ui/ui.reducer';
import { AuthEffects } from './store/auth/auth.effects';
import { DeviceEffects } from './store/device/device.effects';
import { DashboardEffects } from './store/dashboard/dashboard.effects';
import { ExceptionsEffects } from './store/exceptions/exceptions.effects';
import { exceptionsReducer } from './store/exceptions/exceptions.reducer';
import { TelemetryErrorHandler } from './core/error-handler';

function maintenanceCheckFactory(service: MaintenanceCheckService) {
  return () => service.initializeCheck().pipe(take(1)).toPromise();
}

function appConfigFactory(service: AppConfigService) {
  return () => service.load().pipe(take(1)).toPromise();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: TelemetryErrorHandler },
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([
      baseHeadersInterceptor,
      authInterceptor,
      retryInterceptor,
      errorMappingInterceptor,
    ])),
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-top-right',
      timeOut: 3000,
      progressBar: true,
      preventDuplicates: true,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: appConfigFactory,
      deps: [AppConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: maintenanceCheckFactory,
      deps: [MaintenanceCheckService],
      multi: true,
    },
    // NgRx Store configuration
    provideStore({
      auth: authReducer,
      device: deviceReducer,
      dashboard: dashboardReducer,
      ui: uiReducer,
      exceptions: exceptionsReducer,
    }),
    provideEffects([AuthEffects, DeviceEffects, DashboardEffects, ExceptionsEffects]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production, // Read-only in prod; full DevTools in dev/QA
      name: 'ToroOasis NgRx Store', // Name shown in DevTools
    }),
  ],
};
